import React, {useState} from 'react';
import Modal, { useModal } from '../../../Components/Modal';
import Grid from '../../../Components/Formatting/Grid';
import ButtonRow from '../../../Components/Button/ButtonRow';
import Button from '../../../Components/Button/Button';
import { classNames, getEntityLink, plural } from '../../../Services/helpers';
import { statuses, useJobs } from '../../../Data/Jobs';
import Loading, { PageLoading } from '../../../Components/Loading';
import Title from '../../../Components/Formatting/Customer/Title';
import Badge from '../../../Components/Badge/Badge';
import Services from '../../../Components/Formatting/Services';

const reasons = [
    {
        const: 'TIME_WINDOW_CONSTRAINT',
        title: 'Time Window Constraint',
        description: 'The job\'s advanced scheduling rules were outside of working hours, or there wasn\'t enough time available to complete all the jobs.'
    },
    {
        const: 'SHIFT_TIME_CONSTRAINT',
        title: 'Working Time Constraint',
        description: 'The maximum shift time wasn\'t enough to include all the jobs.',
    },
    {
        const: 'MAX_DISTANCE_CONSTRAINT',
        title: 'Distance Constraint',
        description: 'The distance required to travel was too far.',
    },
    {
        const: 'SKILL_CONSTRAINT',
        title: 'Skill Constraint',
        description: 'Jobs required capabilities which weren\'t covered by the available workers.',
    },
    {
        const: 'REACHABLE_CONSTRAINT',
        title: 'Reachable Constraint',
        description: 'It wasn\'t possible to plan routes to the jobs (possibly because of temporary road closures).',
    },
    {
        const: 'NO_REASON_FOUND',
        title: 'Unknown',
        description: 'The reason it wasn\'t possible is unknown.',
    },
    {
        const: 'MISSING_LOCATION',
        title: 'Missing Location',
        description: 'The jobs didn\'t have a location available for route planning. Check the address is correct.',
    },
]

const OptimisationIssues = ({isOpen, setIsOpen, unableToSchedule}) => {
    return <Modal title="Missed jobs" isOpen={isOpen} setIsOpen={setIsOpen} dismissible>
        {(isOpen && unableToSchedule)&& <Inner {...{unableToSchedule}} />}
    </Modal>
}

const Inner = ({unableToSchedule}) => {
    const {closeModal} = useModal();
    const [selectedReason, setSelectedReason] = useState(false);

    return selectedReason ? <JobList {...{selectedReason, unableToSchedule, setSelectedReason, closeModal}} /> :
            <ReasonSummary {...{unableToSchedule, setSelectedReason, closeModal}} />
}

const JobList = ({selectedReason, unableToSchedule, setSelectedReason}) => {
    const reason = reasons.find((reason) => reason.const === selectedReason);
    const jobIds = unableToSchedule[selectedReason];
    const {jobs} = useJobs({jobIds});

    return <>
        <p className="mb-5">
            <strong>{reason.title} - {jobIds.length} {plural(jobIds.length, 'jobs', 'job', 'jobs')}</strong><br />
            <span className="text-muted">{reason.description}</span>
        </p>

        <ul className="flex flex-col divide-y my-3 px-3 rounded-xl border max-h-[50vh] overflow-y-auto">
            {jobs ? jobs.map((job) => <JobPreview job={job} />) : <PageLoading />}
            {(jobs && jobs.length === 0) && <p className="text-center text-muted py-2">No jobs found</p>}
        </ul>

        <ButtonRow arrange="left">
            <Button theme="secondary" label="Back" onClick={() => setSelectedReason(null)} />
        </ButtonRow>
    </>
}

const JobPreview = ({job}) => {
    if (!job) {
        return <li className="py-2"><Loading inline /></li>;
    }

    return <li className="flex flex-row py-2">
        <div className="flex flex-col grow">
            <Title customer={job.customer} className="font-bold" />
            <span>{job.title ?? <Services services={job.services} />}</span>
            <span>
                <Badge className={classNames(statuses[job.status].bgColor, statuses[job.status].contrastTextColor)}
                    title={job.status}
                />
            </span>
        </div>
        <div className="shrink-0">
            <Button theme="light" icon="User" label="Customer" link={getEntityLink('Customers/Customer', job.customerId)} />
        </div>
    </li>
}

const ReasonSummary = ({unableToSchedule, setSelectedReason, closeModal}) => {
    return <>
        <div className="prose prose-sm mb-5">
            <p>
                It was not possible to schedule all the available jobs in the working hours, below are the reasons why.
            </p>
        </div>

        <Grid items={reasons.map((reason) => [
            title(unableToSchedule[reason.const].length),
            <>
                <strong>{reason.title}</strong><br />
                <span className="text-muted">{reason.description}</span><br />
                <Button theme="light" onClick={() => setSelectedReason(reason.const)} label="View jobs" />
            </>,
            !!unableToSchedule[reason.const].length
        ])} />

        <ButtonRow>
            <Button theme="secondary" label="Close" onClick={() => closeModal()} />
        </ButtonRow>
    </>
}

const title = (count) => {
    return <strong>{plural(count, '0 jobs', `${count} job`, `${count} jobs`)}</strong>
}

export default OptimisationIssues;
